// Phase I (I-D): the banner shown on every page while Denwen MSP support (a platform admin) is acting
// as a tenant user. It says who they are acting as, until when, and ends the session.
import { useQueryClient } from '@tanstack/react-query';
import { LifeBuoy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/app/auth';
import { Button } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

export function SupportBanner() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [ending, setEnding] = useState(false);
  const support = user?.support;
  // Re-check the session when it is due to expire, so the banner never outlives the access.
  useEffect(() => {
    if (!support) return;
    const ms = new Date(support.expiresAt).getTime() - Date.now();
    const t = setTimeout(() => void qc.invalidateQueries({ queryKey: ['auth', 'me'] }), Math.max(1000, Math.min(ms + 1000, 2_147_000_000)));
    return () => clearTimeout(t);
  }, [support, qc]);
  if (!support || !user) return null;
  const end = async () => {
    setEnding(true);
    await api('/support-session/end', { method: 'POST' }).catch(() => {});
    qc.clear();
    window.location.assign('/platform/support');
  };
  return (
    <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-danger px-4 py-2 text-sm text-white">
      <LifeBuoy className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        <strong>Support session{support.breakGlass ? ' (break-glass)' : ''}:</strong> {support.adminName}, you are acting as <strong>{user.name}</strong> in <strong>{user.tenant.name}</strong> until{' '}
        {formatDateTime(support.expiresAt)}. Everything you do is on their audit log. Billing, security settings, credentials, payroll submissions and exports are off.
      </span>
      <Button size="sm" variant="secondary" loading={ending} onClick={() => void end()}>
        End support session
      </Button>
    </div>
  );
}
